var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import ArcGISPolygon from '@arcgis/core/geometry/Polygon';
import ArcGISPoint from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { iconRegistry } from '../../../../common/icons';
export const regridParcelLayerURL = "https://tiles.arcgis.com/tiles/KzeiCaQsMoeCfoCq/arcgis/rest/services/Regrid_Nationwide_Parcel_Boundaries_v1/MapServer";
export const arcGISGeocodeUrl = "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer";
export const basemapOptions = [
    { id: 'streets', name: 'Streets' },
    { id: 'satellite', name: 'Satellite' },
    { id: 'hybrid', name: 'Hybrid' },
    { id: 'topo', name: 'Topographic' },
    { id: 'gray', name: 'Light Gray Canvas' },
    { id: 'dark-gray', name: 'Dark Gray Canvas' },
    { id: 'oceans', name: 'Oceans' },
    { id: 'osm', name: 'OpenStreetMap' },
    { id: 'terrain', name: 'Terrain' },
    { id: 'dark-gray-vector', name: 'Dark Gray Vector' },
    { id: 'streets-vector', name: 'Streets Vector' },
    { id: 'streets-night-vector', name: 'Streets Night' },
    { id: 'streets-navigation-vector', name: 'Navigation' },
];
export const defaultSymbols = {
    area: {
        fillColor: [0, 0, 255, 0.25],
        outlineColor: [0, 0, 255],
        outlineWidth: 2,
    },
    areaOutlineOnly: {
        fillColor: [0, 0, 0, 0],
        outlineColor: [0, 0, 255],
        outlineWidth: 2,
    },
    parcel: {
        markerColor: [220, 53, 69],
        markerSize: 12,
    },
};
export const createAreaGraphic = (area, style) => {
    if (area.geometry.length === 0)
        return null;
    const polygon = geographicPolygon(area.geometry[0]);
    return new Graphic({
        geometry: polygon,
        attributes: {
            title: area.title,
            areaId: area.id,
            isArea: true,
        },
        symbol: {
            type: 'simple-fill',
            color: style.fillColor || defaultSymbols.area.fillColor,
            outline: {
                color: style.outlineColor || defaultSymbols.area.outlineColor,
                width: style.outlineWidth || defaultSymbols.area.outlineWidth,
            },
        },
    });
};
export const WGS84 = () => ({ wkid: 4326 });
export const isPoint = (geometry) => (geometry === null || geometry === void 0 ? void 0 : geometry.type) === 'point';
export const isPolygon = (geometry) => (geometry === null || geometry === void 0 ? void 0 : geometry.type) === 'polygon';
export const geographicPoint = (longitude, latitude) => new ArcGISPoint({ longitude, latitude, spatialReference: WGS84() });
export const geographicPolygon = (rings) => new ArcGISPolygon({ rings, spatialReference: WGS84() });
export const toGeographic = (g) => webMercatorUtils.webMercatorToGeographic(g);
export const toMercator = (g) => webMercatorUtils.geographicToWebMercator(g);
export const isWebMercator = (g) => {
    var _a;
    const wkid = (_a = g.spatialReference) === null || _a === void 0 ? void 0 : _a.wkid;
    return wkid === 102100 || wkid === 3857;
};
export const isGeographic = (g) => { var _a; return ((_a = g.spatialReference) === null || _a === void 0 ? void 0 : _a.wkid) === 4326; };
export const assertGeographic = (g) => {
    var _a;
    if (isGeographic(g))
        return g;
    if (isWebMercator(g))
        return toGeographic(g);
    throw new Error(`Unsupported spatial reference: ${(_a = g.spatialReference) === null || _a === void 0 ? void 0 : _a.wkid}`);
};
export const hexToRgb = (hex) => {
    const cleaned = hex.replace('#', '');
    return [
        parseInt(cleaned.substring(0, 2), 16),
        parseInt(cleaned.substring(2, 4), 16),
        parseInt(cleaned.substring(4, 6), 16),
    ];
};
// --- Canvas-based marker with kind icon + color ---
const markerUrlCache = new Map();
const markerUrlPromiseCache = new Map();
/** Render a react-icons component to an SVG data URL */
const renderIconSvgUrl = (iconName, size, color) => {
    const IconComponent = iconRegistry[iconName];
    if (!IconComponent)
        return null;
    const svg = renderToStaticMarkup(React.createElement(IconComponent, { size, color }));
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};
/** Load an image from a URL, returning a promise */
const loadImage = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
});
/**
 * Create a data-URL PNG for a colored circle marker with an optional icon. `hollow` draws the
 * same marker inside out — pale fill, colored ring and icon — for a place the map knows the
 * location of but holds no contributed data on.
 */
export const createMarkerUrl = (hexColor_1, iconName_1, ...args_1) => __awaiter(void 0, [hexColor_1, iconName_1, ...args_1], void 0, function* (hexColor, iconName, size = 48, hollow = false) {
    const key = `${hexColor}-${iconName || ''}-${size}-${hollow}`;
    if (markerUrlCache.has(key))
        return markerUrlCache.get(key);
    if (markerUrlPromiseCache.has(key))
        return markerUrlPromiseCache.get(key);
    const promise = (() => __awaiter(void 0, void 0, void 0, function* () {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        const rgb = hexToRgb(hexColor);
        const color = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
        // Colored circle
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
        ctx.fillStyle = hollow ? 'rgba(255,255,255,0.9)' : color;
        ctx.fill();
        ctx.strokeStyle = hollow ? color : 'white';
        ctx.lineWidth = hollow ? 3.5 : 2.5;
        ctx.stroke();
        // Icon from SVG
        if (iconName) {
            const iconSize = Math.round(size * 0.5);
            const svgUrl = renderIconSvgUrl(iconName, iconSize, hollow ? color : 'white');
            if (svgUrl) {
                try {
                    const img = yield loadImage(svgUrl);
                    const offset = (size - iconSize) / 2;
                    ctx.drawImage(img, offset, offset, iconSize, iconSize);
                }
                catch (_a) {
                    // icon failed to load, marker still gets the colored circle
                }
            }
        }
        const url = canvas.toDataURL('image/png');
        markerUrlCache.set(key, url);
        return url;
    }))();
    markerUrlPromiseCache.set(key, promise);
    try {
        return yield promise;
    }
    finally {
        markerUrlPromiseCache.delete(key);
    }
});
/** Build an area SymbolStyle from the district's --color-primary CSS variable */
export const getDistrictAreaStyle = () => {
    const primary = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim();
    if (!primary)
        return defaultSymbols.area;
    const rgb = hexToRgb(primary);
    return {
        fillColor: [rgb[0], rgb[1], rgb[2], 0.25],
        outlineColor: rgb,
        outlineWidth: 2,
    };
};
export const createGraphic = (x, mapItem, mapItemToMarker, mapItemToAttributes, style) => __awaiter(void 0, void 0, void 0, function* () {
    const item = mapItem(x);
    const marker = mapItemToMarker(x);
    const markerSize = style.markerSize || defaultSymbols.parcel.markerSize;
    const attributes = mapItemToAttributes(x);
    // Only the explore map distinguishes the two; every other map's items are contributed.
    const claimed = attributes.claimed !== 0;
    return new Graphic({
        geometry: geographicPoint(item.point[0], item.point[1]),
        attributes: Object.assign({ 'itemId': item.id, 'pointX': item.point[0], 'pointY': item.point[1], 'kindName': (marker === null || marker === void 0 ? void 0 : marker.name) || null, 'kindIcon': (marker === null || marker === void 0 ? void 0 : marker.icon) || null }, attributes),
        symbol: (marker === null || marker === void 0 ? void 0 : marker.color)
            ? {
                type: 'picture-marker',
                url: yield createMarkerUrl(marker.color, marker.icon || undefined, 48, !claimed),
                width: markerSize * 2,
                height: markerSize * 2,
            }
            : {
                type: 'simple-marker',
                color: claimed ? (style.markerColor || defaultSymbols.parcel.markerColor) : [255, 255, 255, 0.9],
                size: markerSize,
                outline: claimed
                    ? { color: [255, 255, 255], width: 2 }
                    : { color: style.markerColor || defaultSymbols.parcel.markerColor, width: 2.5 },
            },
    });
});
export const createDisplayConfig = (overrides) => (Object.assign({ basemap: 'streets', center: undefined, zoom: 12, height: '100%', width: '100%', symbols: defaultSymbols, showLegend: true }, overrides));
